import { publicEnv } from '@/lib/env';
import {
  ACHIEVEMENT_FILTER_OPTIONS,
  DEFAULT_ACHIEVEMENT_FILTER,
} from '@/modules/players/v3/constants/achievements';
import type {
  Achievement,
  AchievementFilter,
  AchievementMilestone,
} from '@/modules/players/v3/types/achievements';
import { AchievementType } from '@/types/achievement.types';

const VALID_ACHIEVEMENT_FILTERS = new Set<AchievementFilter>(
  ACHIEVEMENT_FILTER_OPTIONS.map((option) => option.value)
);

/** Normalizes an arbitrary stored value into a valid filter, falling back to the default. */
export function parseAchievementFilter(value: string | undefined | null): AchievementFilter {
  return value && VALID_ACHIEVEMENT_FILTERS.has(value as AchievementFilter)
    ? (value as AchievementFilter)
    : DEFAULT_ACHIEVEMENT_FILTER;
}

/** Filters that the API can resolve server-side; the rest are derived client-side. */
export function achievementFilterToApiType(filter: AchievementFilter): AchievementType | undefined {
  if (filter === 'almost_there') return AchievementType.ALMOST_FINISHED;
  if (filter === 'latest') return AchievementType.LATEST_ACHIEVED;
  return undefined;
}

export function getNormalizedAchievementMilestones(achievement: {
  milestones: AchievementMilestone[];
  objectiveDescription: string;
  goalValue: number;
  currentValue: number;
  alreadyAchieved?: boolean;
}): AchievementMilestone[] {
  if (achievement.milestones.length > 0) {
    return achievement.milestones;
  }

  const achieved =
    achievement.alreadyAchieved !== undefined
      ? achievement.alreadyAchieved
      : achievement.currentValue >= achievement.goalValue;

  return [
    {
      displayOrder: 1,
      name: achievement.objectiveDescription,
      thresholdValue: achievement.goalValue,
      achieved,
      achievedAt: undefined,
    },
  ];
}

/**
 * Filters achievements by the selected state. `latest` and `almost_there` are normally resolved
 * by the API, but the client-side equivalents are kept here so the helper works for any filter.
 */
export function filterAchievementsByState(
  achievements: Achievement[],
  filter: AchievementFilter
): Achievement[] {
  switch (filter) {
    case 'latest':
      return achievements.filter((a) => a.alreadyAchieved);
    case 'almost_there':
      return achievements.filter((a) => !a.alreadyAchieved && a.currentValue > 0);
    case 'not_started':
      return achievements.filter((a) => !a.alreadyAchieved && a.currentValue === 0);
    case 'all':
    default:
      return achievements;
  }
}

export function groupAchievementsByCategoryId(
  achievements: Achievement[]
): Map<number, Achievement[]> {
  const grouped = new Map<number, Achievement[]>();
  for (const a of achievements) {
    const list = grouped.get(a.categoryId) ?? [];
    list.push(a);
    grouped.set(a.categoryId, list);
  }
  return grouped;
}

export function getAchievementShareUrl(slug: string | string[], achievementId: number): string {
  const base = `https://${publicEnv.NEXT_PUBLIC_HOME_ROOT_URL}/players/${slug}/achievements`;
  const params = new URLSearchParams({ achievementId: String(achievementId) });
  return `${base}?${params.toString()}`;
}
